import React, { useRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useTenant } from '../contexts/TenantContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { formatDateIST, formatTimeIST } from '../utils/date';

function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

const SECTION_LABELS = {
  chief_complaints: 'Chief Complaints',
  past_history: 'Past History',
  tumor_board: 'Tumor Board',
  clinical_examination: 'Clinical Examination',
  provisional_diagnosis: 'Provisional Diagnosis',
  final_diagnosis: 'Final Diagnosis',
  treatment_plan: 'Treatment Plan',
  visit_notes: 'Notes',
  review_date: 'Follow-up'
};

const INTAKE_LABELS = {
  before_food: 'Before Food',
  after_food: 'After Food',
  with_food: 'With Food',
  empty_stomach: 'Empty Stomach',
  any_time: 'Any Time',
};

function formatTimingForPrint(med) {
  // Doses are stored either as a parallel `timing_doses` map (1, 0.5, or
  // omitted) or implicitly 1 for each entry in `timing`. Render as a
  // dash-joined string M-A-E-N where slots are "1", "½", or "0". Half
  // doses are written as "½" so the printed Rx mirrors the on-screen
  // "1/2 tablet" intent without needing fractions parsed by the patient.
  const order = ['M', 'A', 'E', 'N'];
  const doses = med.timing_doses || {};
  const set = new Set(med.timing || []);
  return order
    .map(c => {
      const d = doses[c];
      if (d === 0.5) return '½';
      if (d === 1) return '1';
      if (d === 0 || d === undefined) return set.has(c) ? '1' : '0';
      return String(d);
    })
    .join('-');
}

export function PrescriptionPrint({ consultation: consultationProp, selectedPatient, patientDetails, onClose, onAfterPrint }) {
  const printRef = useRef(null);
  // Per-tenant branding for the printed letterhead. Reading from tenant
  // context means a clinic admin uploads once via /admin/branding and
  // every doctor's prescriptions automatically use it.
  const { tenant } = useTenant();
  const tBranding = tenant?.branding || {};
  const clinicName = tBranding.display_name || tenant?.name || 'Your Clinic';
  const rxLogoUrl = tBranding.prescription_logo_url || tBranding.logo_url || '';
  const letterhead = tBranding.letterhead_text || '';
  const footerText = tBranding.prescription_footer_text || '';
  // Always refetch the latest consultation from the server when the modal opens
  // so the print output reflects the newest items/sections even if the parent's
  // React state update hadn't committed when the modal was requested.
  const [consultation, setConsultation] = useState(consultationProp);
  // Doctor signature block — qualifications/registration come from the
  // canonical hospital profile (users.ProfessionalInfo), not the local
  // user row. Fetched once when the modal opens so the printed Rx
  // always carries the medico-legal footer line.
  const [doctorProfile, setDoctorProfile] = useState(null);
  // Discharge summary — for IP visits we fetch the hospital's official
  // discharge summary (rendered HTML) so the doctor can print a
  // consolidated Rx + discharge document in one go instead of handling
  // two separate prints. OP / CR visits skip this fetch.
  const [dischargeSummary, setDischargeSummary] = useState(null);
  // Header mode — doctors often print on pre-printed letterhead paper that
  // already has the hospital branding printed. Default to "none" which omits
  // the digital header and reserves ~38mm blank space at the top for the
  // pre-printed content. Flip to "full" to print the full branded header.
  const [headerMode, setHeaderMode] = useState('none'); // 'none' | 'full'
  useEffect(() => {
    const id = consultationProp?.id;
    if (!id) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/consultations/${id}`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled && data) setConsultation(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [consultationProp?.id]);

  // Pull qualifications / registration once per print session. The
  // consultation document doesn't carry these (they live on the doctor's
  // professional profile) so we fetch them lazily here.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/doctors/me/profile`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled && data) setDoctorProfile(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Discharge summary lookup — only for IP visits. The
  // appointment-details endpoint already fetches and converts the
  // .docx → HTML server-side, so we just consume `discharge_summary`
  // from its response. Failures silently degrade — the rest of the
  // print still renders.
  useEffect(() => {
    const appointmentId = consultationProp?.appointment_id;
    const visitType = (selectedPatient?.visit_type || '').toUpperCase();
    if (!appointmentId || visitType !== 'IP') {
      setDischargeSummary(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API_URL}/api/appointments/${appointmentId}/details`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled && data?.discharge_summary) {
          setDischargeSummary(data.discharge_summary);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [consultationProp?.appointment_id, selectedPatient?.visit_type]);
  const consultationId = consultation?.id || '';
  const [prescriptionUrl, setPrescriptionUrl] = useState(
    consultationId ? `${API_URL}/api/prescription/${consultationId}/view` : '',
  );

  useEffect(() => {
    if (!consultationId) return undefined;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/consultations/${consultationId}/public-view-url`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled && data?.url) setPrescriptionUrl(data.url);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [consultationId]);
  const sections = consultation?.sections || {};
  const today = formatDateIST(new Date().toISOString());
  const timeNow = formatTimeIST(new Date());

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Prescription - ${consultation.patient_name}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Manrope', sans-serif; color: #1a1a1a; background: white; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            @page { margin: 14mm 14mm; size: A4; }
            /* Prevent any prescription/investigation row from being cut by a
               page break — a row split across two pages is the #1 cause of
               "missing medicine" reports from doctors. Repeat the header on
               every printed page so the column meaning stays visible. */
            table { page-break-inside: auto; }
            thead { display: table-header-group; }
            tbody { display: table-row-group; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            /* Keep multi-row blocks (medicine + its remarks sub-row) together */
            .rx-keep-together { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    // Once the print dialog closes (afterprint event in the popup) fire the
    // parent's onAfterPrint so the doctor can be auto-routed back to the
    // dashboard for the next patient. Fallback: a 600ms delayed close +
    // callback in case the popup blocks the afterprint event.
    let fired = false;
    const fireOnce = () => {
      if (fired) return;
      fired = true;
      try { printWindow.close(); } catch (_) {}
      if (typeof onAfterPrint === 'function') onAfterPrint();
    };
    try {
      printWindow.addEventListener('afterprint', fireOnce);
    } catch (_) { /* cross-origin / popup blocker — fallback below */ }
    setTimeout(() => { try { printWindow.print(); } catch (_) {} }, 600);
    // Safety net: if afterprint never fires (some browsers / blocked popups),
    // give the user 60s with the print dialog before we still bounce them
    // back. Plenty of time for paper-printer dialogs.
    setTimeout(fireOnce, 60000);
  };

  // Gather filled sections
  const filledSections = [];
  Object.entries(SECTION_LABELS).forEach(([key, label]) => {
    const sec = sections[key];
    if (!sec) return;
    const content = sec.content;
    if (key === 'review_date') {
      if (sec.date) filledSections.push({ key, label, type: 'date', date: sec.date });
      return;
    }
    const text = stripHtml(content);
    if (text) filledSections.push({ key, label, type: 'html', content });
  });

  const prescriptionItems = sections.prescription?.items || [];
  // Defensive: coerce any object-shaped legacy investigation entries
  // (`{name: 'CBC'}`) to strings before rendering, otherwise React #31
  // crashes the print dialog. The InvestigationsSection load-time
  // healer fixes this on the next save, but printing can happen
  // before that runs.
  const investigationItems = (sections.investigations?.items || [])
    .map((t) => (typeof t === 'string' ? t : ((t && (t.name || t.test_name)) || '')))
    .filter(Boolean);

  // Vitals — pulled from general_info (auto-populated from appointment vitals
  // on consultation open, user-editable). Printed as a dense grid above the
  // clinical sections so the printed Rx always has the latest vitals context.
  const vitalsData = (sections.general_info?.content && typeof sections.general_info.content === 'object')
    ? sections.general_info.content
    : (typeof sections.general_info === 'object' ? sections.general_info : {});

  // Referral — set on the Wrap-up section. Printed near the bottom of
  // the Rx so the receiving doctor / patient sees who they're being
  // referred to without scrolling through prescription rows.
  const referralData = (sections.referral?.data && typeof sections.referral.data === 'object')
    ? sections.referral.data
    : null;
  const hasReferral = !!(referralData && (
    (referralData.referred_to_doctor_name || '').trim() ||
    (referralData.referred_to_specialization || '').trim() ||
    (referralData.remarks || '').trim()
  ));
  const vitalsList = [
    ['BP', vitalsData.bp],
    ['Pulse', vitalsData.pulse && `${vitalsData.pulse} bpm`],
    ['Temp', vitalsData.temperature && `${vitalsData.temperature}°F`],
    ['SpO2', vitalsData.spo2 && `${vitalsData.spo2}%`],
    ['Resp', vitalsData.respiration_rate && `${vitalsData.respiration_rate}/min`],
    ['Weight', vitalsData.weight && `${vitalsData.weight} kg`],
    ['Height', vitalsData.height && `${vitalsData.height} cm`],
    ['BMI', vitalsData.bmi],
  ].filter(([, v]) => v);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center" data-testid="print-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[900px] h-[90vh] flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>Prescription Preview</h2>
          <div className="flex items-center gap-3">
            {/* Header mode toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-testid="header-mode-toggle">
              <button
                onClick={() => setHeaderMode('none')}
                data-testid="header-mode-none"
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  headerMode === 'none' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Reserve blank space at the top for your pre-printed letterhead"
              >
                Pre-printed letterhead
              </button>
              <button
                onClick={() => setHeaderMode('full')}
                data-testid="header-mode-full"
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  headerMode === 'full' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Print the full digital hospital header"
              >
                With header
              </button>
            </div>
            <Button onClick={handlePrint} size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-sm" data-testid="print-action-btn">
              <Printer className="h-4 w-4" /> Print
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="close-print-modal">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto bg-gray-100 p-6">
          <div ref={printRef} style={{ maxWidth: '794px', margin: '0 auto', background: 'white', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', fontFamily: "'Manrope', sans-serif" }}>
            
            {/* Hospital Header — hidden when printing on pre-printed letterhead */}
            {headerMode === 'full' ? (
              <div style={{ background: 'linear-gradient(135deg, #071B34 0%, #0C2744 55%, #F15A22 160%)', color: 'white', padding: '20px 28px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  {rxLogoUrl ? (
                    <img src={rxLogoUrl} alt="clinic logo"
                         style={{ width: '52px', height: '52px', objectFit: 'contain', background: 'rgba(255,255,255,0.95)', borderRadius: '12px', padding: '4px', border: '2px solid rgba(255,255,255,0.2)' }} />
                  ) : (
                    <div style={{ width: '52px', height: '52px', background: 'rgba(255,255,255,0.15)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 800, letterSpacing: '-1px', border: '2px solid rgba(255,255,255,0.2)' }}>
                      {(clinicName || 'C').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '1px', marginBottom: '2px' }}>{clinicName.toUpperCase()}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {(letterhead || 'Address · Phone · Email').split('\n').map((line, i) => (
                    <div key={i} style={{ fontSize: '10px', opacity: 0.9, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{line}</div>
                  ))}
                </div>
              </div>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.2)', margin: '0 -28px' }}></div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
                <span style={{ fontSize: '26px', fontWeight: 800, fontStyle: 'italic', opacity: 0.9 }}>Rx</span>
                <span style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '4px', textTransform: 'uppercase' }}>PRESCRIPTION</span>
                <span style={{ fontSize: '11px', opacity: 0.85, fontWeight: 500 }}>{today} | {timeNow}</span>
              </div>
            </div>
            ) : (
              // Pre-printed stationery mode — reserve ~38mm for the physical
              // letterhead already printed on the paper. Keep a minimal Rx
              // strip at the bottom of the reserved zone so doctors can see
              // the prescription identity in the preview, but print it as
              // plain text (no colored bar) so it blends with the letterhead.
              <div style={{ paddingTop: '38mm' }}>
                <div style={{
                  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                  padding: '0 28px 8px', borderBottom: '1.5px solid #dbeafe'
                }}>
                  <span style={{ fontSize: '22px', fontWeight: 800, fontStyle: 'italic', color: '#071B34' }}>Rx</span>
                  <span style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: '#071B34' }}>PRESCRIPTION</span>
                  <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500 }}>{today} | {timeNow}</span>
                </div>
              </div>
            )}

            {/* Patient Information */}
            <div style={{
              padding: '20px 28px',
              background: headerMode === 'full' ? '#f0fdf4' : '#ffffff',
              borderBottom: headerMode === 'full' ? '2px solid #3b82c8' : '1px solid #e5e7eb',
              marginTop: headerMode === 'full' ? '0' : '6px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px 20px' }}>
                {[
                  ['Patient Name', consultation.patient_name || 'N/A'],
                  ['MRN', selectedPatient?.mrn || patientDetails?.medical_record_number || 'N/A'],
                  ['OP Number', selectedPatient?.appointment_id || 'N/A'],
                  ['Visit Type', selectedPatient?.visit_type || 'OP'],
                  ['Phone', selectedPatient?.phone || patientDetails?.phone || 'N/A'],
                  // Age + Gender — surfaced as a single field so the
                  // 4-col grid stays balanced. Falls back to dash when
                  // either piece is missing rather than blanking the row.
                  ['Age / Gender', (() => {
                    const age = selectedPatient?.age ?? consultation.patient_age;
                    const gender = selectedPatient?.gender || consultation.patient_gender || '';
                    if (!age && !gender) return 'N/A';
                    const ageStr = age ? `${age} yrs` : '—';
                    return gender ? `${ageStr} / ${gender}` : ageStr;
                  })()],
                  ['Consulting Doctor', `Dr. ${consultation.doctor_name || 'N/A'}`],
                  ['Department', selectedPatient?.specialisation || 'General'],
                  // Patient's payer/scheme — printed alongside Department so
                  // the pharmacy + receiving clinic immediately know which
                  // tier the billing should land at. Only renders when set
                  // (walk-in cash patients won't have a scheme).
                  ...(selectedPatient?.scheme ? [['Scheme', selectedPatient.scheme]] : []),
                  ['Date', today]
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#071B34', marginBottom: '2px' }}>{label}</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a1a' }}>{value}</span>
                  </div>
                ))}
              </div>
              {/* Fee / discount line — rendered only when reception applied
                  a discount or a full waiver. Prints below the demographics
                  grid so the receiving clinic + pharmacy see the billing
                  context alongside the clinical record. */}
              {consultation.fee && (consultation.fee.discount_amount > 0 || consultation.fee.waived) && (
                <div style={{
                  marginTop: '10px', padding: '8px 10px',
                  background: consultation.fee.waived ? '#fef3c7' : '#eef2ff',
                  border: `1px solid ${consultation.fee.waived ? '#fcd34d' : '#c7d2fe'}`,
                  borderRadius: '6px',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  fontSize: '11px',
                }}>
                  <span style={{
                    fontSize: '9px', fontWeight: 700, letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    color: consultation.fee.waived ? '#92400e' : '#3730a3',
                  }}>
                    Consultation Fee
                  </span>
                  <span style={{ fontWeight: 700, color: '#111' }}>
                    ₹{Math.round(consultation.fee.final_fee || 0)}
                  </span>
                  {consultation.fee.waived ? (
                    <span style={{ color: '#92400e', fontWeight: 600 }}>
                      · Waived{consultation.fee.reason ? ` — ${consultation.fee.reason.replace('_', ' ')}` : ''}
                    </span>
                  ) : (
                    <span style={{ color: '#3730a3' }}>
                      · was ₹{Math.round(consultation.fee.base_fee || 0)} · {Math.round(consultation.fee.discount_percent || 0)}% off
                      {consultation.fee.reason ? ` (${consultation.fee.reason.replace('_', ' ')})` : ''}
                    </span>
                  )}
                  {consultation.fee.note && (
                    <span style={{ color: '#4b5563', fontStyle: 'italic', marginLeft: 'auto' }}>
                      {consultation.fee.note}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Clinical Sections */}
            <div style={{ padding: '26px 28px 20px', minHeight: '400px' }}>
              {/* Vitals — printed immediately after patient info so the clinical
                  context is visible upfront, before narrative sections */}
              {vitalsList.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#071B34', borderBottom: '1.5px solid #dbeafe', paddingBottom: '6px', marginBottom: '12px' }}>Vitals</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px 18px', paddingLeft: '4px' }}>
                    {vitalsList.map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>{label}</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {filledSections.map(sec => (
                <div key={sec.key} style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#071B34', borderBottom: '1.5px solid #dbeafe', paddingBottom: '6px', marginBottom: '12px' }}>{sec.label}</div>
                  {sec.type === 'date' ? (
                    <div style={{ fontSize: '12px', lineHeight: 1.7, color: '#374151', paddingLeft: '8px' }}>{formatDateIST(sec.date)}</div>
                  ) : (
                    <div style={{ fontSize: '12px', lineHeight: 1.7, color: '#374151', paddingLeft: '8px' }} dangerouslySetInnerHTML={{ __html: sec.content }} />
                  )}
                </div>
              ))}

              {/* Investigations — rendered as compact, elegant tag chips, ABOVE the prescription */}
              {investigationItems.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px',
                    color: '#071B34', borderBottom: '1.5px solid #dbeafe', paddingBottom: '6px', marginBottom: '12px'
                  }}>
                    <span>Investigations Ordered</span>
                    <span style={{
                      background: '#071B34', color: 'white', borderRadius: '999px',
                      padding: '2px 8px', fontSize: '9px', letterSpacing: '0.5px'
                    }}>
                      {investigationItems.length} {investigationItems.length === 1 ? 'Test' : 'Tests'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 8px' }}>
                    {investigationItems.map((inv, i) => {
                      const name = typeof inv === 'string' ? inv : (inv.name || inv.test_name || '');
                      if (!name) return null;
                      return (
                        <span key={i} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '4px 10px',
                          background: 'linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)',
                          border: '1px solid #bfdbfe', borderRadius: '999px',
                          fontSize: '11px', fontWeight: 600, color: '#071B34', letterSpacing: '0.2px'
                        }}>
                          <span style={{
                            display: 'inline-block', width: '5px', height: '5px',
                            borderRadius: '50%', background: '#2563eb'
                          }} />
                          {name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Prescription Table — Remarks rendered as an elegant sub-row under each medicine */}
              {prescriptionItems.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#071B34', borderBottom: '1.5px solid #dbeafe', paddingBottom: '6px', marginBottom: '12px' }}>Prescription</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr>
                        {['#', 'Medicine', 'Dosage', 'Frequency', 'Intake', 'Timing', 'Duration', 'Qty'].map(h => (
                          <th key={h} style={{ background: '#071B34', color: 'white', padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {prescriptionItems.map((med, i) => {
                        const timingStr = formatTimingForPrint(med);
                        const intakeLabel = INTAKE_LABELS[med.intake] || (med.intake ? String(med.intake).replace(/_/g, ' ') : '-');
                        const hasRemarks = med.remarks && String(med.remarks).trim();
                        const stripe = i % 2 === 1 ? '#f9fafb' : 'white';
                        return (
                          <React.Fragment key={i}>
                            <tr style={{ background: stripe, pageBreakInside: 'avoid' }}>
                              <td style={{ padding: '8px 10px', borderBottom: hasRemarks ? 'none' : '1px solid #e5e7eb', verticalAlign: 'top' }}>{i + 1}</td>
                              <td style={{ padding: '8px 10px', borderBottom: hasRemarks ? 'none' : '1px solid #e5e7eb', fontWeight: 600, color: '#111827', verticalAlign: 'top', wordBreak: 'break-word' }}>{med.medicine_name || med.name || '(no name)'}</td>
                              <td style={{ padding: '8px 10px', borderBottom: hasRemarks ? 'none' : '1px solid #e5e7eb', verticalAlign: 'top' }}>{med.dosage || '-'}</td>
                              <td style={{ padding: '8px 10px', borderBottom: hasRemarks ? 'none' : '1px solid #e5e7eb', verticalAlign: 'top' }}>{med.frequency || '-'}</td>
                              <td style={{ padding: '8px 10px', borderBottom: hasRemarks ? 'none' : '1px solid #e5e7eb', verticalAlign: 'top' }}>{intakeLabel}</td>
                              <td style={{ padding: '8px 10px', borderBottom: hasRemarks ? 'none' : '1px solid #e5e7eb', fontFamily: 'monospace', letterSpacing: '1px', verticalAlign: 'top' }}>{timingStr}</td>
                              <td style={{ padding: '8px 10px', borderBottom: hasRemarks ? 'none' : '1px solid #e5e7eb', verticalAlign: 'top' }}>{med.duration || '-'}</td>
                              <td style={{ padding: '8px 10px', borderBottom: hasRemarks ? 'none' : '1px solid #e5e7eb', verticalAlign: 'top' }}>{med.quantity || '-'}</td>
                            </tr>
                            {hasRemarks && (
                              <tr style={{ background: stripe, pageBreakInside: 'avoid' }}>
                                <td style={{ borderBottom: '1px solid #e5e7eb' }}></td>
                                <td colSpan={7} style={{ padding: '0 10px 8px', borderBottom: '1px solid #e5e7eb' }}>
                                  <div style={{
                                    display: 'inline-flex', alignItems: 'flex-start', gap: '8px',
                                    padding: '5px 10px',
                                    background: '#fffbeb',
                                    borderLeft: '2px solid #f59e0b',
                                    borderRadius: '0 4px 4px 0',
                                    maxWidth: '100%',
                                  }}>
                                    <span style={{
                                      fontSize: '9px', fontWeight: 700, textTransform: 'uppercase',
                                      letterSpacing: '0.5px', color: '#b45309', marginTop: '1px',
                                      flexShrink: 0,
                                    }}>Remarks</span>
                                    <span style={{ fontSize: '11px', color: '#451a03', lineHeight: 1.5 }}>{med.remarks}</span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {filledSections.length === 0 && prescriptionItems.length === 0 && investigationItems.length === 0 && !hasReferral && (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>No clinical data entered yet</div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>Fill in consultation sections to generate the prescription</div>
                </div>
              )}

              {/* Discharge Summary — IP visits only. Renders the
                  hospital's official discharge document inline so the
                  printed Rx doubles as the patient's hand-off
                  document. Mammoth has already converted the .docx
                  into clean HTML server-side; we inject it via
                  dangerouslySetInnerHTML and force readable typography
                  via the wrapper styles below.
                  Hidden when discharge HTML is empty (older OP-style
                  IP visits without a discharge module entry). */}
              {dischargeSummary && (dischargeSummary.content || '').replace(/<[^>]+>/g, '').trim() && (
                <div data-testid="print-discharge-block" style={{
                  marginTop: '14px',
                  padding: '12px 16px',
                  border: '1.5px solid #10b981',
                  borderLeft: '4px solid #10b981',
                  borderRadius: '6px',
                  background: '#ecfdf5',
                  pageBreakInside: 'avoid',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: 800,
                      color: '#065f46',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      background: '#a7f3d0',
                      padding: '3px 10px',
                      borderRadius: '3px',
                    }}>Discharge Summary</span>
                    {dischargeSummary.ip_number && (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: '#065f46', fontFamily: 'monospace' }}>
                        {dischargeSummary.ip_number}
                      </span>
                    )}
                  </div>
                  <div
                    className="print-discharge-html"
                    style={{
                      fontSize: '11.5px',
                      lineHeight: 1.5,
                      color: '#111827',
                      maxWidth: '100%',
                      overflowWrap: 'break-word',
                      wordWrap: 'break-word',
                    }}
                    dangerouslySetInnerHTML={{ __html: dischargeSummary.content }}
                  />
                  <style>{`
                    .print-discharge-html p { margin: 0 0 6px 0; }
                    .print-discharge-html strong { color: #065f46; font-weight: 700; }
                    .print-discharge-html h1, .print-discharge-html h2, .print-discharge-html h3 {
                      font-size: 12.5px; font-weight: 800; color: #065f46;
                      margin: 8px 0 4px 0;
                    }
                    .print-discharge-html ul, .print-discharge-html ol { margin: 4px 0 6px 18px; padding: 0; }
                    .print-discharge-html li { margin: 1px 0; }
                    .print-discharge-html table { border-collapse: collapse; width: 100%; margin: 4px 0; font-size: 10.5px; }
                    .print-discharge-html td, .print-discharge-html th {
                      border: 1px solid #d1fae5; padding: 3px 6px; vertical-align: top;
                    }
                  `}</style>
                </div>
              )}

              {/* Referral — printed near the bottom so the receiving
                  doctor's coordinates are easy to spot. Only renders
                  when the consulting doctor actually referred this
                  patient onwards. */}
              {hasReferral && (
                <div data-testid="print-referral-block" style={{
                  marginTop: '14px',
                  padding: '10px 14px',
                  border: '1.5px solid #f59e0b',
                  borderLeft: '4px solid #f59e0b',
                  borderRadius: '6px',
                  background: '#fffbeb',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: 800,
                      color: '#92400e',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      background: '#fde68a',
                      padding: '2px 8px',
                      borderRadius: '3px',
                    }}>Referral</span>
                    <span style={{ fontSize: '10.5px', fontWeight: 600, color: '#92400e' }}>
                      Patient referred to:
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                    {referralData.referred_to_doctor_name
                      ? <>Dr. {referralData.referred_to_doctor_name}</>
                      : <span style={{ color: '#6b7280', fontStyle: 'italic', fontWeight: 500 }}>(Doctor not specified)</span>}
                    {referralData.referred_to_specialization && (
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#071B34', marginLeft: '8px' }}>
                        · {referralData.referred_to_specialization}
                      </span>
                    )}
                  </div>
                  {/* Extended contact block — hospital, address, phone,
                      email — surfaces when the doctor referred to a
                      newly-added external doctor with full contact info.
                      Resident referrals just show name + specialty. */}
                  {(referralData.referred_to_hospital
                    || referralData.referred_to_address
                    || referralData.referred_to_phone
                    || referralData.referred_to_email) && (
                    <div style={{
                      marginTop: '4px',
                      fontSize: '10.5px',
                      color: '#475569',
                      lineHeight: 1.5,
                    }}>
                      {referralData.referred_to_hospital && (
                        <div><strong>Hospital:</strong> {referralData.referred_to_hospital}</div>
                      )}
                      {referralData.referred_to_address && (
                        <div><strong>Address:</strong> {referralData.referred_to_address}</div>
                      )}
                      {(referralData.referred_to_phone || referralData.referred_to_email) && (
                        <div>
                          {referralData.referred_to_phone && (<><strong>Ph:</strong> {referralData.referred_to_phone}</>)}
                          {referralData.referred_to_phone && referralData.referred_to_email && <span style={{ margin: '0 6px' }}>·</span>}
                          {referralData.referred_to_email && (<><strong>Email:</strong> {referralData.referred_to_email}</>)}
                        </div>
                      )}
                    </div>
                  )}
                  {referralData.remarks && (
                    <div style={{
                      marginTop: '4px',
                      fontSize: '11px',
                      color: '#374151',
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                    }}>
                      <span style={{ fontWeight: 700, color: '#6b7280' }}>Reason / Remarks:</span> {referralData.remarks}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ borderTop: headerMode === 'full' ? '2px solid #3b82c8' : '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', padding: '20px 28px 12px' }}>
                {/* QR Code */}
                <div style={{ textAlign: 'center', flexShrink: 0 }}>
                  <QRCodeSVG value={prescriptionUrl} size={80} level="M" includeMargin={false} />
                  <div style={{ fontSize: '8px', color: '#6b7280', marginTop: '4px', fontWeight: 500 }}>Scan for Digital Copy</div>
                </div>

                {/* Disclaimer */}
                <div style={{ flex: 1, padding: '0 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '9px', color: '#6b7280', lineHeight: 1.5 }}>This is a computer-generated prescription. Please follow the prescribed medication as directed.</div>
                  <div style={{ fontSize: '9px', color: '#6b7280', lineHeight: 1.5 }}>In case of any adverse reaction, contact the hospital immediately.</div>
                </div>

                {/* Signature — doctor identity for the medico-legal footer.
                    Pulls qualification + registration from the canonical
                    hospital profile so the printed Rx always carries the
                    same credentials the regulator expects. */}
                <div style={{ textAlign: 'center', flexShrink: 0, minWidth: '200px', maxWidth: '260px' }}>
                  <div style={{ width: '100%', height: '1px', background: '#374151', marginBottom: '6px', marginTop: '40px' }}></div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>Dr. {consultation.doctor_name}</div>
                  {doctorProfile?.qualification_line && (
                    <div style={{ fontSize: '9px', color: '#374151', fontWeight: 600, marginTop: '1px', lineHeight: 1.35 }}>
                      {doctorProfile.qualification_line}
                    </div>
                  )}
                  <div style={{ fontSize: '10px', color: '#071B34', fontWeight: 600, marginTop: '1px' }}>
                    {doctorProfile?.specialization || selectedPatient?.specialisation || 'Consultant'}
                  </div>
                  {doctorProfile?.registration_number && (
                    <div style={{ fontSize: '8.5px', color: '#4b5563', fontWeight: 500, marginTop: '1px' }}>
                      Reg. No: {doctorProfile.registration_number}
                    </div>
                  )}
                  <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>{today} | {timeNow}</div>
                </div>
              </div>

              {/* Tenant-defined footer text (signature line / disclaimer). */}
              {footerText && (
                <div style={{
                  padding: '10px 28px',
                  borderTop: '1px solid #e5e7eb',
                  fontSize: '9px',
                  color: '#374151',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.5,
                }}>
                  {footerText}
                </div>
              )}

              {/* Copyright Bar — hidden in pre-printed mode (letterhead has branding) */}
              {headerMode === 'full' && (
                <div style={{ background: '#071B34', color: 'white', padding: '8px 28px', display: 'flex', justifyContent: 'space-between', fontSize: '8px' }}>
                  <span>&copy; {new Date().getFullYear()} {clinicName}. All rights reserved.</span>
                  <span>Powered by ECare 360</span>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
